import React, { useState, useEffect, Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { PerspectiveCamera } from '@react-three/drei';
import * as THREE from 'three';
// import NeuralNetwork from './NeuralNetwork'; // Temporarily disabled - has TypeScript errors
import Option1ParticleField from './Option1_ParticleField';
import Option2GeometricGrid from './Option2_GeometricGrid';
import Option3DNAHelix from './Option3_DNAHelix';
import Option4FloatingSpheres from './Option4_FloatingSpheres';
import Option5HolographicRings from './Option5_HolographicRings';

// CHANGE THIS NUMBER (0-5) TO TEST DIFFERENT STYLES
// 0 = Original Neural Network
// 1 = Particle Field (2000 floating particles)
// 2 = Geometric Grid (Wave distortion grid)
// 3 = DNA Helix (Double helix structure)
// 4 = Floating Spheres (30 orbiting spheres)
// 5 = Holographic Rings (12 concentric rings)
const ACTIVE_OPTION: number = 5;

const HeroNeuralScene: React.FC = () => {
  const [mousePosition, setMousePosition] = useState(new THREE.Vector2(0, 0));
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);

    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition(
        new THREE.Vector2(
          (e.clientX / window.innerWidth) * 2 - 1,
          -(e.clientY / window.innerHeight) * 2 + 1
        )
      );
    };

    const handleScroll = () => {
      const scrolled = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const progress = Math.min(scrolled / Math.max(maxScroll, 1), 1);
      setScrollProgress(progress);
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none'
      }}
    >
      <Canvas
        dpr={[1, isMobile ? 1.5 : 2]}
        gl={{
          alpha: true,
          antialias: !isMobile,
          powerPreference: 'high-performance'
        }}
      >
        <Suspense fallback={null}>
          <PerspectiveCamera makeDefault position={[0, 0, 10]} fov={50} />

          {/* Lighting */}
          <ambientLight intensity={0.3} />
          <pointLight position={[10, 10, 10]} intensity={0.8} color="#00d4ff" />
          <pointLight position={[-10, -10, -10]} intensity={0.5} color="#b24bf3" />

          {/* Render selected option */}
          {/* Option 0 temporarily disabled due to TypeScript errors */}
          {ACTIVE_OPTION === 1 && (
            <Option1ParticleField
              scrollProgress={scrollProgress}
              mousePosition={mousePosition}
            />
          )}
          {ACTIVE_OPTION === 2 && (
            <Option2GeometricGrid
              scrollProgress={scrollProgress}
              mousePosition={mousePosition}
            />
          )}
          {ACTIVE_OPTION === 3 && (
            <Option3DNAHelix
              scrollProgress={scrollProgress}
              mousePosition={mousePosition}
            />
          )}
          {ACTIVE_OPTION === 4 && (
            <Option4FloatingSpheres
              scrollProgress={scrollProgress}
              mousePosition={mousePosition}
            />
          )}
          {ACTIVE_OPTION === 5 && (
            <Option5HolographicRings
              scrollProgress={scrollProgress}
              mousePosition={mousePosition}
            />
          )}
        </Suspense>
      </Canvas>
    </div>
  );
};

export default HeroNeuralScene;
